import {
  Trophy, Target, Wallet, Sparkles, ShieldCheck, ScrollText, Users, BookOpen,
  Award, Calculator, Zap, Globe2, Cpu, Layers, GitCompare, TrendingUp, FileCheck,
} from "lucide-react";
import GenericInfoPage from "@/pages/GenericInfoPage";

/* All "info" sub-pages registered from a single config so we keep the
   tree small. Each export is a route component. */

const PLATFORM_RELATED = [
  { to: "/platforms/mt5",         label: "MetaTrader 5",           tagline: "Desktop · web · mobile" },
  { to: "/platforms/web",         label: "MT5 Web Terminal",       tagline: "Trade from any browser" },
  { to: "/platforms/mobile",      label: "MT5 Mobile",             tagline: "iOS & Android apps" },
  { to: "/account-comparison",    label: "Compare Accounts",       tagline: "Classic · Premier · Elite · Social" },
];

const ACCOUNT_RELATED = [
  { to: "/account-comparison",    label: "All Account Types",      tagline: "Classic · Premier · Elite · Social" },
  { to: "/first-deposit-bonus",   label: "50% Deposit Bonus",      tagline: "On your first deposit" },
  { to: "/calculator",            label: "Trading Calculators",    tagline: "Pip · margin · profit" },
  { to: "/live-spreads",          label: "Live Spreads",           tagline: "Real-time across all assets" },
  { to: "/faq",                   label: "Client FAQ",             tagline: "Common questions answered" },
];

const PARTNER_RELATED = [
  { to: "/partner",               label: "Partner Program",        tagline: "IB & affiliate rebates" },
  { to: "/affiliates/influencers",label: "Influencer Track",       tagline: "For content creators" },
  { to: "/affiliates/dashboard",  label: "Partner Dashboard",      tagline: "Track clicks & payouts" },
  { to: "/contact",               label: "Talk to Us",             tagline: "Custom partner deals" },
];

export function HowItWorksPage() {
  return (
    <GenericInfoPage
      testId="page-how-it-works"
      pill="How It Works"
      title="From sign up to your first trade ·"
      accent="three steps"
      intro="OFinancial Markets is a multi-asset broker for serious traders. Open an account, fund it and trade 1200+ instruments on MetaTrader 5 from raw spreads of 0.0 pips."
      breadcrumbs={[{ label: "How It Works" }]}
      sections={[
        { Icon: Target,   h: "Step 1 · Open an account",      body: "Pick the account type that matches your style · Classic, Premier, Elite raw-spread or Social copy-trading. Onboarding takes minutes with simple KYC.",                bullets: ["Classic from $50", "Elite raw spreads", "Social copy-trading"] },
        { Icon: Sparkles, h: "Step 2 · Fund your account",    body: "Deposit instantly via card, bank wire, USDT, USDC or local methods. Your first deposit qualifies for a 50% tradable credit bonus.",                                  bullets: ["Card · bank · crypto", "50% First Deposit Bonus", "Same-day funding"] },
        { Icon: Wallet,   h: "Step 3 · Trade on MT5",         body: "Download MetaTrader 5 for Windows, Mac, web or mobile. Sign in and trade 1200+ instruments with the same login on every device.",                                   bullets: ["MT5 desktop · web · mobile", "Single login everywhere", "EAs & copy-trading"] },
      ]}
      ctaPrimary="Open Account"
      ctaPrimaryHref="/account-comparison"
      ctaSecondary="See Account Types"
      ctaSecondaryHref="/account-comparison"
      related={ACCOUNT_RELATED}
    />
  );
}

export function GeneralRulesPage() {
  return (
    <GenericInfoPage
      testId="page-general-rules"
      pill="Trading Conditions"
      title="Transparent conditions ·"
      accent="every account"
      intro="OFinancial Markets publishes the same execution model, spreads schedule and swap rates for every client. Read once, trade everywhere on MT5."
      breadcrumbs={[{ label: "Trading Conditions" }]}
      sections={[
        { Icon: ShieldCheck, h: "Execution",           body: "Market execution on Premier, Elite and Social accounts. Sub-millisecond latency, no requotes and no last-look on majors." },
        { Icon: Target,      h: "Spreads & commission", body: "Floating spreads from 0.0 pips on Elite raw accounts, with a low commission per side. Classic and Social accounts run commission-free with a slightly wider spread.", bullets: ["Elite · raw spread + commission", "Classic · commission-free", "1200+ instruments"] },
        { Icon: Wallet,      h: "Funding & withdrawals", body: "Deposit instantly via card, bank wire, USDT, USDC and local methods. Same-day withdrawal processing on most methods · no minimum hold." },
        { Icon: ScrollText,  h: "Trading conduct",      body: "All strategies welcome · scalping, hedging, EAs, copy-trading. We only prohibit abusive arbitrage, market manipulation and prohibited jurisdictions.", bullets: ["EAs / algo trading: allowed", "Hedging: allowed", "Scalping: allowed"] },
      ]}
      ctaPrimary="Open Account"
      ctaPrimaryHref="/account-comparison"
      ctaSecondary="View FAQ"
      ctaSecondaryHref="/faq"
      related={ACCOUNT_RELATED}
    />
  );
}

export function AffiliatesPage() {
  return (
    <GenericInfoPage
      testId="page-affiliates"
      pill="Partner Program"
      title="Earn with"
      accent="OFinancial Markets"
      intro="Per-lot rebates on every active client you refer · plus a CPA structure for high-volume partners. Real-time dashboard, daily payouts, dedicated partner team."
      breadcrumbs={[{ label: "Partners" }]}
      sections={[
        { Icon: Users,    h: "Per-lot rebates",       body: "Earn a share of the spread on every lot your active referred clients trade · ongoing, no take-backs, no drop-off after month one.", bullets: ["Per-lot rebates", "Paid on active referred clients", "No drop-off after month one"] },
        { Icon: Trophy,   h: "CPA available",         body: "High-volume partners can opt into a CPA on every active trader · negotiated case-by-case. Email partners@ofinancial.markets once approved.", bullets: ["CPA per active trader", "Negotiated per partner", "Stacked rebates available"] },
        { Icon: Wallet,   h: "Payouts",               body: "Daily payouts on Introducer · paid every business day in USDT, USDC, USD wire or local bank. $50 minimum withdrawal. Ambassador retainers are paid monthly on the first Tuesday." },
        { Icon: BookOpen, h: "Creative assets",       body: "Logos, banners, video B-roll, copy templates, MT5 demo screenshots and a translation kit for seven languages." },
      ]}
      ctaPrimary="Open Partner Page"
      ctaPrimaryHref="/partner"
      ctaSecondary="Talk to Us"
      ctaSecondaryHref="/contact"
      related={PARTNER_RELATED}
    />
  );
}

export function PayoutProtectorPage() {
  return (
    <GenericInfoPage
      testId="page-payout-protector"
      pill="Add-on"
      title="Negative Balance"
      accent="Protection"
      intro="OFinancial Markets applies negative balance protection on every retail client account. Your loss can never exceed your deposited balance · period."
      breadcrumbs={[{ label: "Protection" }]}
      sections={[
        { Icon: ShieldCheck, h: "What is covered",   body: "Any equity that goes below zero from a sudden market gap or stop-out is automatically reset to zero · we absorb the negative balance on your behalf." },
        { Icon: Sparkles,    h: "How to enable it",   body: "It is applied automatically to all retail client accounts at OFinancial Markets · no opt-in required, no extra cost." },
        { Icon: Award,       h: "Who it covers",      body: "Available on every Classic, Premier, Elite and Social retail account. Professional clients can request the same protection in writing." },
      ]}
      ctaPrimary="Open Account"
      ctaPrimaryHref="/account-comparison"
      related={ACCOUNT_RELATED}
    />
  );
}

export function CalculatorPage() {
  return (
    <GenericInfoPage
      testId="page-calculator"
      pill="Trading Calculators"
      title="Pip · margin ·"
      accent="profit"
      intro="Estimate pip value, required margin and projected profit across every OFinancial Markets account before you place the trade."
      breadcrumbs={[{ label: "Calculators" }]}
      sections={[
        { Icon: Calculator, h: "How it is calculated", body: "Margin = Trade size × Contract size × Price / Leverage. Pip value = Pip size × Trade size × Contract size. Profit = Pips × Pip value. The calculator does not predict performance · past results are not indicative of future results." },
      ]}
      ctaPrimary="Open Full Calculator"
      ctaPrimaryHref="/#calculator"
      related={ACCOUNT_RELATED}
    />
  );
}

export function BlogPage() {
  return null; // Removed per institutional-broker content policy · no
               // page should exist without real authored content.
}

export function ContactPage() {
  return (
    <GenericInfoPage
      testId="page-contact"
      pill="Contact"
      title="Talk to"
      accent="OFinancial Markets"
      intro="Reach our team via email or the live contact form on the home page. We respond within 4 business hours."
      breadcrumbs={[{ label: "Contact" }]}
      sections={[
        { Icon: Users, h: "General support", body: "support@ofinancial.markets" },
        { Icon: Users, h: "Partners",        body: "partners@ofinancial.markets" },
        { Icon: Users, h: "Press & media",   body: "press@ofinancial.markets" },
        { Icon: Users, h: "Refunds",         body: "refunds@ofinancial.markets" },
      ]}
      ctaPrimary="Open Contact Form"
      ctaPrimaryHref="/#contact"
      related={[
        { to: "/faq",        label: "FAQ",                       tagline: "Client FAQ" },
        { to: "/about",      label: "About OFinancial Markets",  tagline: "Who we are" },
        { to: "/legal/terms",label: "Terms",                     tagline: "Legal documents" },
      ]}
    />
  );
}

export function AboutPage() {
  return (
    <GenericInfoPage
      testId="page-about"
      pill="About"
      title="The OFinancial Markets"
      accent="story"
      intro="OFinancial Markets is a multi-asset broker built for serious traders · focused on transparent pricing, fast execution and great client service."
      breadcrumbs={[{ label: "About" }]}
      sections={[
        { Icon: BookOpen,    h: "Who we are",  body: "Founded by ex-institutional traders and platform engineers, OFinancial Markets was built to fix the things active traders kept complaining about at retail brokers: wide spreads, slow withdrawals and the constant feeling that the house is running against them." },
        { Icon: Target,      h: "What we do",  body: "We provide MT5 access to 1200+ instruments with raw spreads from 0.0 pips, dynamic leverage up to 1:1000 and segregated client funds. No tricks, no hidden conditions." },
        { Icon: ShieldCheck, h: "Where we operate", body: "Globally, except restricted jurisdictions. Our dispute resolution venue is published in the Client Agreement." },
      ]}
      ctaPrimary="Open Account"
      ctaPrimaryHref="/account-comparison"
      ctaSecondary="See Trading Conditions"
      ctaSecondaryHref="/general-rules"
      related={[
        { to: "/how-it-works",       label: "How It Works",       tagline: "From sign-up to MT5" },
        { to: "/account-comparison", label: "Account Types",      tagline: "Pick your account" },
        { to: "/legal/terms",        label: "Terms & Conditions", tagline: "Legal documents" },
        { to: "/contact",            label: "Contact",            tagline: "Talk to us" },
      ]}
    />
  );
}

/* Legacy platform landing pages · we now ship MT5 only at OFinancial
   Markets, but we keep these routes alive for inbound links. Every
   page redirects the trader to the MT5 platform page. */
function PlatformLanding({ name, slug, tagline, hero, bullets }) {
  return (
    <GenericInfoPage
      testId={`page-platform-${slug}`}
      pill="Platform"
      title={name}
      accent={tagline}
      intro={hero}
      breadcrumbs={[
        { label: "Platforms", to: "/platforms/mt5" },
        { label: name },
      ]}
      sections={[
        { Icon: Sparkles,   h: `About ${name}`,                  body: tagline, bullets },
        { Icon: Target,     h: "We use MetaTrader 5",            body: "OFinancial Markets has chosen MetaTrader 5 as our sole trading platform · the global standard for multi-asset retail trading. Every order, every chart and every EA you need lives in one app, synced across desktop, web and mobile." },
        { Icon: GitCompare, h: "Open an MT5 account",            body: "Pick an account type · Classic, Premier, Elite or Social · and trade FX, metals, indices, crypto and shares CFDs from a single MT5 login." },
      ]}
      ctaPrimary="Open Account"
      ctaPrimaryHref="/account-comparison"
      ctaSecondary="See MT5 Platform"
      ctaSecondaryHref="/platforms/mt5"
      related={PLATFORM_RELATED}
    />
  );
}

export function WebTradingPage() {
  return (
    <GenericInfoPage
      testId="page-web-trading"
      pill="Web Trading"
      title="Trade from"
      accent="any browser"
      intro="OFinancial Markets offers the MetaTrader 5 Web Terminal · a full-featured browser-based MT5 client. No download, no install · sign in and trade."
      breadcrumbs={[
        { label: "Platforms", to: "/platforms/mt5" },
        { label: "Web Terminal" },
      ]}
      sections={[
        { Icon: Globe2,     h: "Zero install",      body: "Click the Web Terminal link, sign in with your MT5 credentials and you are trading. Switch devices mid-session without losing layouts." },
        { Icon: ShieldCheck,h: "Same security",     body: "The Web Terminal uses the same authentication and encryption as desktop MT5. No reduced functionality, no degraded order types." },
        { Icon: Layers,     h: "Full MT5 features", body: "Charting, EAs, order management, depth of market and one-click trading · all in the browser.", bullets: ["Full MT5 charting", "Order management", "EAs supported via desktop"] },
      ]}
      ctaPrimary="Open MT5"
      ctaPrimaryHref="/platforms/mt5"
      ctaSecondary="Open Account"
      ctaSecondaryHref="/account-comparison"
      related={PLATFORM_RELATED}
    />
  );
}

export function MobileTradingPage() {
  return (
    <GenericInfoPage
      testId="page-mobile-trading"
      pill="Mobile"
      title="OFinancial Markets in your"
      accent="pocket"
      intro="Native iOS and Android MetaTrader 5 apps. Full charting, one-tap trade management, push alerts and biometric login."
      breadcrumbs={[
        { label: "Platforms", to: "/platforms/mt5" },
        { label: "Mobile Apps" },
      ]}
      sections={[
        { Icon: Sparkles,    h: "Full-feature MT5 apps", body: "Charting, order placement, position management, news and account stats · everything desktop MT5 offers, optimised for thumb-driven UX." },
        { Icon: ShieldCheck, h: "Biometric login",       body: "Face ID / Touch ID / fingerprint unlock for fast and secure session resume. No password fatigue between sessions." },
        { Icon: Cpu,         h: "Price alerts",           body: "Opt-in push notifications when prices cross configurable levels. Stay on top of the market, even on the move." },
      ]}
      ctaPrimary="Open MT5 Mobile"
      ctaPrimaryHref="/platforms/mt5"
      ctaSecondary="Open Account"
      ctaSecondaryHref="/account-comparison"
      related={PLATFORM_RELATED}
    />
  );
}

export function AffiliateDashboardPage() {
  return (
    <GenericInfoPage
      testId="page-affiliate-dashboard"
      pill="Partner Dashboard"
      title="Your"
      accent="commission HQ"
      intro="Real-time click tracking, conversion stats, balance, payout history, and creative download. Sign in with your OFinancial Markets partner credentials."
      breadcrumbs={[
        { label: "Partners", to: "/affiliates" },
        { label: "Dashboard" },
      ]}
      sections={[
        { Icon: TrendingUp, h: "Sign in or apply",        body: "Existing partners can sign in via the partner portal. New applicants · email partners@ofinancial.markets with your channel details; most applications are approved within 24 business hours." },
        { Icon: FileCheck,  h: "What you will see",       body: "Live click counts, conversion-to-funded rates, geographic breakdowns, cohort retention and a one-click creative library download." },
        { Icon: Wallet,     h: "Payouts in the dashboard",body: "Daily Introducer payouts via USDT, USDC, USD wire or Wise. Ambassador retainers are paid monthly on the first Tuesday. Request a payout from the balance widget; we approve within 24h." },
      ]}
      ctaPrimary="Apply Now"
      ctaPrimaryHref="/partner"
      related={PARTNER_RELATED}
    />
  );
}

export function InfluencersPage() {
  return (
    <GenericInfoPage
      testId="page-influencers"
      pill="Influencer Program"
      title="For creators"
      accent="of all sizes"
      intro="Whether you have 10K followers on YouTube or 100K on TikTok, the OFinancial Markets creator program meets you with custom commissions, dedicated assets and a real human account manager."
      breadcrumbs={[
        { label: "Partners", to: "/affiliates" },
        { label: "Influencers" },
      ]}
      sections={[
        { Icon: Trophy,   h: "Stacked rewards",     body: "Per-lot rebates on every active referred client · plus an optional CPA structure, negotiated case-by-case." },
        { Icon: Users,    h: "Account manager",     body: "Your AM helps with content briefs, exclusive promo codes, sponsorship opportunities and timely payouts." },
        { Icon: BookOpen, h: "Asset library",       body: "Stock footage, brand kit, copy templates, comparison cheatsheets · everything you need for high-converting content." },
        { Icon: Zap,      h: "Co-branded content",  body: "Top creators are invited to co-produce campaigns · funded by OFinancial Markets, featuring you. Real production budgets, real creative freedom." },
      ]}
      ctaPrimary="Apply Now"
      ctaPrimaryHref="/partner"
      ctaSecondary="Contact Us"
      ctaSecondaryHref="/contact"
      related={PARTNER_RELATED}
    />
  );
}
